export type LearningVisualKind =
  | 'task'
  | 'constraint'
  | 'rights'
  | 'institution'
  | 'history'
  | 'society'
  | 'state'
  | 'everyday'
  | 'answer';


export interface LearningNode {
  kind: LearningVisualKind;
  label: string;
  german: string;
  meaning: string;
}


export interface LearningCopy {
  title: string;
  subtitle: string;
  show: string;
  hide: string;
  questionPattern: string;
  attention: string;
  topic: string;
  proof: string;
  followFlow: string;
  proofAfter: string;
  correctAnswer: string;
  appearance: string;
  light: string;
  dark: string;
}


const LANGUAGES = [
  'de', 'tr', 'en', 'ar', 'pl', 'uk', 'ru', 'sq',
  'es', 'fa', 'id', 'fr', 'kmr', 'ro', 'bg', 'it',
  'hr', 'sr', 'prs', 'bs', 'vi', 'hu', 'el',
] as const;


const COPY_KEYS = [
  'title',
  'subtitle',
  'show',
  'hide',
  'questionPattern',
  'attention',
  'topic',
  'proof',
  'followFlow',
  'proofAfter',
  'correctAnswer',
  'appearance',
  'light',
  'dark',
] as const;


const COPY_RAW: Record<string, string> = {
  de: 'Fragemuster|Vom Muster zur Antwort|Öffnen|Ausblenden|Fragemuster|Wichtiger Hinweis|Thema|Antwortbeleg|Von links nach rechts lesen|Beleg nach der Antwort|Richtige Antwort|Darstellung|Hell|Dunkel',
  tr: 'Kalıp Haritası|Kalıptan cevaba|Aç|Gizle|Soru kalıbı|Önemli ayrıntı|Konu|Cevap kanıtı|Soldan sağa bağlantıyı izle|Kanıt cevaptan sonra açılır|Doğru cevap|Görünüm|Açık|Koyu',
  en: 'Pattern Map|From pattern to answer|Open|Hide|Question pattern|Important detail|Topic|Answer evidence|Follow the link from left to right|Evidence opens after answering|Correct answer|Appearance|Light|Dark',
  ar: 'خريطة النمط|من النمط إلى الإجابة|فتح|إخفاء|نمط السؤال|تفصيل مهم|الموضوع|دليل الإجابة|اتبع الرابط من اليسار إلى اليمين|يظهر الدليل بعد الإجابة|الإجابة الصحيحة|المظهر|فاتح|داكن',
  pl: 'Mapa wzorca|Od wzorca do odpowiedzi|Otwórz|Ukryj|Wzorzec pytania|Ważny szczegół|Temat|Dowód odpowiedzi|Śledź połączenie od lewej do prawej|Dowód pojawi się po odpowiedzi|Prawidłowa odpowiedź|Wygląd|Jasny|Ciemny',
  uk: 'Карта шаблону|Від шаблону до відповіді|Відкрити|Сховати|Шаблон питання|Важлива деталь|Тема|Доказ відповіді|Стежте за зв’язком зліва направо|Доказ з’явиться після відповіді|Правильна відповідь|Вигляд|Світлий|Темний',
  ru: 'Карта шаблона|От шаблона к ответу|Открыть|Скрыть|Шаблон вопроса|Важная деталь|Тема|Доказательство ответа|Следуйте по связи слева направо|Доказательство появится после ответа|Правильный ответ|Оформление|Светлое|Тёмное',
  sq: 'Harta e modelit|Nga modeli te përgjigjja|Hap|Fshih|Modeli i pyetjes|Detaj i rëndësishëm|Tema|Prova e përgjigjes|Ndiq lidhjen nga e majta në të djathtë|Prova shfaqet pas përgjigjes|Përgjigjja e saktë|Pamja|E çelët|E errët',
  es: 'Mapa del patrón|Del patrón a la respuesta|Abrir|Ocultar|Patrón de pregunta|Detalle importante|Tema|Prueba de respuesta|Sigue la conexión de izquierda a derecha|La prueba aparece después de responder|Respuesta correcta|Apariencia|Claro|Oscuro',
  fa: 'نقشه الگو|از الگو تا پاسخ|باز کردن|پنهان کردن|الگوی پرسش|نکته مهم|موضوع|مدرک پاسخ|پیوند را از چپ به راست دنبال کنید|مدرک پس از پاسخ باز می‌شود|پاسخ درست|ظاهر|روشن|تیره',
  id: 'Peta Pola|Dari pola ke jawaban|Buka|Sembunyikan|Pola pertanyaan|Detail penting|Topik|Bukti jawaban|Ikuti hubungan dari kiri ke kanan|Bukti muncul setelah menjawab|Jawaban benar|Tampilan|Terang|Gelap',
  fr: 'Carte du modèle|Du modèle à la réponse|Ouvrir|Masquer|Forme de la question|Détail important|Thème|Preuve de la réponse|Suivez le lien de gauche à droite|La preuve apparaît après la réponse|Bonne réponse|Apparence|Clair|Sombre',
  kmr: 'Nexşeya qalibê|Ji qalibê ber bi bersivê|Veke|Veşêre|Qalibê pirsê|Hûrguliya girîng|Mijar|Delîla bersivê|Girêdanê ji çepê ber bi rastê bişopîne|Delîl piştî bersivê vedibe|Bersiva rast|Xuyanî|Ronî|Tarî',
  ro: 'Harta tiparului|De la tipar la răspuns|Deschide|Ascunde|Tiparul întrebării|Detaliu important|Temă|Dovada răspunsului|Urmărește legătura de la stânga la dreapta|Dovada apare după răspuns|Răspuns corect|Aspect|Luminos|Întunecat',
  bg: 'Карта на модела|От модела към отговора|Отвори|Скрий|Модел на въпроса|Важна подробност|Тема|Доказателство за отговора|Следвай връзката отляво надясно|Доказателството се показва след отговор|Правилен отговор|Изглед|Светъл|Тъмен',
  it: 'Mappa dello schema|Dallo schema alla risposta|Apri|Nascondi|Schema della domanda|Dettaglio importante|Tema|Prova della risposta|Segui il collegamento da sinistra a destra|La prova appare dopo la risposta|Risposta corretta|Aspetto|Chiaro|Scuro',
  hr: 'Karta obrasca|Od obrasca do odgovora|Otvori|Sakrij|Obrazac pitanja|Važan detalj|Tema|Dokaz odgovora|Prati vezu slijeva nadesno|Dokaz se prikazuje nakon odgovora|Točan odgovor|Izgled|Svijetlo|Tamno',
  sr: 'Mapa obrasca|Od obrasca do odgovora|Otvori|Sakrij|Obrazac pitanja|Važan detalj|Tema|Dokaz odgovora|Prati vezu sleva nadesno|Dokaz se prikazuje nakon odgovora|Tačan odgovor|Izgled|Svetlo|Tamno',
  prs: 'نقشه الگو|از الگو تا پاسخ|باز کردن|پنهان کردن|الگوی پرسش|نکته مهم|موضوع|مدرک پاسخ|پیوند را از چپ به راست دنبال کنید|مدرک پس از پاسخ باز می‌شود|پاسخ درست|ظاهر|روشن|تیره',
  bs: 'Karta obrasca|Od obrasca do odgovora|Otvori|Sakrij|Obrazac pitanja|Važan detalj|Tema|Dokaz odgovora|Prati vezu slijeva nadesno|Dokaz se prikazuje nakon odgovora|Tačan odgovor|Izgled|Svijetlo|Tamno',
  vi: 'Bản đồ mẫu câu|Từ mẫu câu đến đáp án|Mở|Ẩn|Mẫu câu hỏi|Chi tiết quan trọng|Chủ đề|Bằng chứng đáp án|Theo dõi liên kết từ trái sang phải|Bằng chứng hiện sau khi trả lời|Đáp án đúng|Giao diện|Sáng|Tối',
  hu: 'Mintatérkép|A mintától a válaszig|Megnyitás|Elrejtés|Kérdésminta|Fontos részlet|Téma|Válasz bizonyítéka|Kövesd a kapcsolatot balról jobbra|A bizonyíték válaszadás után jelenik meg|Helyes válasz|Megjelenés|Világos|Sötét',
  el: 'Χάρτης μοτίβου|Από το μοτίβο στην απάντηση|Άνοιγμα|Απόκρυψη|Μοτίβο ερώτησης|Σημαντική λεπτομέρεια|Θέμα|Απόδειξη απάντησης|Ακολούθησε τη σύνδεση από αριστερά προς τα δεξιά|Η απόδειξη εμφανίζεται μετά την απάντηση|Σωστή απάντηση|Εμφάνιση|Φωτεινό|Σκοτεινό',
};


const TERM_RAW: Record<string, string> = {
  which: 'welche?|hangisi?|which?|أي؟|który?|який?|какой?|cili?|¿cuál?|کدام؟|yang mana?|lequel?|kîjan?|care?|кой?|quale?|koji?|koji?|کدام؟|koji?|cái nào?|melyik?|ποιο;',
  who: 'wer?|kim?|who?|من؟|kto?|хто?|кто?|kush?|¿quién?|چه کسی؟|siapa?|qui?|kî?|cine?|кой?|chi?|tko?|ko?|کی؟|ko?|ai?|ki?|ποιος;',
  what: 'was?|ne?|what?|ماذا؟|co?|що?|что?|çfarë?|¿qué?|چه؟|apa?|quoi?|çi?|ce?|какво?|cosa?|što?|šta?|چه؟|šta?|gì?|mi?|τι;',
  where: 'wo?|nerede?|where?|أين؟|gdzie?|де?|где?|ku?|¿dónde?|کجا؟|di mana?|où?|li ku?|unde?|къде?|dove?|gdje?|gde?|کجا؟|gdje?|ở đâu?|hol?|πού;',
  when: 'wann?|ne zaman?|when?|متى؟|kiedy?|коли?|когда?|kur?|¿cuándo?|چه زمانی؟|kapan?|quand?|kengî?|când?|кога?|quando?|kada?|kada?|چه وقت؟|kada?|khi nào?|mikor?|πότε;',
  how: 'wie?|nasıl?|how?|كيف؟|jak?|як?|как?|si?|¿cómo?|چگونه؟|bagaimana?|comment?|çawa?|cum?|как?|come?|kako?|kako?|چگونه؟|kako?|như thế nào?|hogyan?|πώς;',
  why: 'warum?|neden?|why?|لماذا؟|dlaczego?|чому?|почему?|pse?|¿por qué?|چرا؟|mengapa?|pourquoi?|çima?|de ce?|защо?|perché?|zašto?|zašto?|چرا؟|zašto?|tại sao?|miért?|γιατί;',
  permission: 'darf?|izin var mı?|may?|هل يُسمح؟|czy wolno?|чи дозволено?|разрешено ли?|a lejohet?|¿se permite?|آیا مجاز است؟|bolehkah?|est-ce permis?|destûr heye?|este permis?|позволено ли е?|è permesso?|je li dopušteno?|da li je dozvoljeno?|آیا اجازه است؟|je li dozvoljeno?|có được phép?|szabad?|επιτρέπεται;',
  obligation: 'muss?|zorunlu mu?|must?|هل يجب؟|czy trzeba?|чи потрібно?|обязательно ли?|a duhet?|¿debe?|آیا باید؟|haruskah?|faut-il?|divê?|trebuie?|трябва ли?|deve?|mora li?|mora li?|آیا باید؟|mora li?|có phải?|kell?|πρέπει;',
  ability: 'kann?|mümkün mü?|can?|هل يمكن؟|czy można?|чи можна?|можно ли?|a mundet?|¿puede?|آیا می‌توان؟|bisakah?|peut-on?|dikare?|se poate?|може ли?|può?|može li?|može li?|آیا می‌شود؟|može li?|có thể?|lehet?|μπορεί;',
  negative: 'nicht / kein|değil / hiçbir|not / no|ليس / لا|nie / żaden|не / жоден|не / никакой|jo / asnjë|no / ninguno|نه / هیچ|tidak / bukan|ne…pas / aucun|ne / tu|nu / niciun|не / никакъв|non / nessuno|ne / nijedan|ne / nijedan|نه / هیچ|ne / nijedan|không|nem / semmilyen|όχι / κανένα',
  only: 'nur|yalnızca|only|فقط|tylko|лише|только|vetëm|solo|فقط|hanya|seulement|tenê|doar|само|solo|samo|samo|فقط|samo|chỉ|csak|μόνο',
  except: 'außer|hariç|except|باستثناء|oprócz|крім|кроме|përveç|excepto|به‌جز|kecuali|sauf|ji bilî|cu excepția|освен|tranne|osim|osim|به‌جز|osim|ngoại trừ|kivéve|εκτός',
  because: 'weil / denn|çünkü|because|لأن|ponieważ|тому що|потому что|sepse|porque|زیرا|karena|parce que|ji ber ku|deoarece|защото|perché|jer|jer|زیرا|jer|bởi vì|mert|επειδή',
  purpose: 'damit|böylece|so that|لكي|aby|щоб|чтобы|që|para que|تا|agar|afin que|da ku|pentru ca|за да|affinché|kako bi|da bi|تا|kako bi|để|azért hogy|ώστε',
  condition: 'wenn / falls|eğer|if|إذا|jeśli|якщо|если|nëse|si|اگر|jika|si|heke|dacă|ако|se|ako|ako|اگر|ako|nếu|ha|αν',
  contrast: 'obwohl / aber|rağmen / ama|although / but|رغم أن / لكن|chociaż / ale|хоча / але|хотя / но|megjithëse / por|aunque / pero|اگرچه / اما|meskipun / tetapi|bien que / mais|her çend / lê|deși / dar|въпреки че / но|sebbene / ma|iako / ali|iako / ali|اگرچه / اما|iako / ali|mặc dù / nhưng|bár / de|αν και / αλλά',
  sequence: 'zuerst / danach|önce / sonra|first / then|أولاً / ثم|najpierw / potem|спочатку / потім|сначала / потом|së pari / pastaj|primero / después|اول / سپس|pertama / kemudian|d’abord / ensuite|pêşî / paşê|mai întâi / apoi|първо / после|prima / dopo|prvo / zatim|prvo / zatim|اول / سپس|prvo / zatim|trước / sau đó|először / azután|πρώτα / μετά',
  rights: 'Rechte & Freiheit|Haklar ve özgürlük|Rights & freedom|الحقوق والحرية|Prawa i wolność|Права і свобода|Права и свобода|Të drejtat dhe liria|Derechos y libertad|حقوق و آزادی|Hak dan kebebasan|Droits et liberté|Maf û azadî|Drepturi și libertate|Права и свобода|Diritti e libertà|Prava i sloboda|Prava i sloboda|حقوق و آزادی|Prava i sloboda|Quyền và tự do|Jogok és szabadság|Δικαιώματα και ελευθερία',
  institutions: 'Staat & Institutionen|Devlet ve kurumlar|State & institutions|الدولة والمؤسسات|Państwo i instytucje|Держава та установи|Государство и учреждения|Shteti dhe institucionet|Estado e instituciones|دولت و نهادها|Negara dan lembaga|État et institutions|Dewlet û sazî|Stat și instituții|Държава и институции|Stato e istituzioni|Država i institucije|Država i institucije|دولت و نهادها|Država i institucije|Nhà nước và thể chế|Állam és intézmények|Κράτος και θεσμοί',
  history: 'Geschichte & Zeit|Tarih ve zaman|History & time|التاريخ والزمن|Historia i czas|Історія і час|История и время|Historia dhe koha|Historia y tiempo|تاریخ و زمان|Sejarah dan waktu|Histoire et temps|Dîrok û dem|Istorie și timp|История и време|Storia e tempo|Povijest i vrijeme|Istorija i vreme|تاریخ و زمان|Historija i vrijeme|Lịch sử và thời gian|Történelem és idő|Ιστορία και χρόνος',
  society: 'Gesellschaft & Alltag|Toplum ve günlük yaşam|Society & daily life|المجتمع والحياة اليومية|Społeczeństwo i codzienność|Суспільство і повсякдення|Общество и повседневность|Shoqëria dhe përditshmëria|Sociedad y vida diaria|جامعه و زندگی روزمره|Masyarakat dan keseharian|Société et quotidien|Civak û jiyana rojane|Societate și viață cotidiană|Общество и ежедневие|Società e vita quotidiana|Društvo i svakodnevica|Društvo i svakodnevica|جامعه و زندگی روزمره|Društvo i svakodnevica|Xã hội và đời sống|Társadalom és mindennapok|Κοινωνία και καθημερινότητα',
  federalState: 'Land & Kommune|Eyalet ve belediye|State & municipality|الولاية والبلدية|Land i gmina|Земля і громада|Земля и муниципалитет|Landi dhe komuna|Estado y municipio|ایالت و شهرداری|Negara bagian dan kota|Land et commune|Eyalet û şaredarî|Land și comună|Провинция и община|Land e comune|Pokrajina i općina|Pokrajina i opština|ایالت و شهرداری|Pokrajina i općina|Bang và đô thị|Tartomány és település|Κρατίδιο και δήμος',
  everyday: 'Deutschland & Alltag|Almanya ve günlük yaşam|Germany & daily life|ألمانيا والحياة اليومية|Niemcy i codzienność|Німеччина і повсякдення|Германия и повседневность|Gjermania dhe përditshmëria|Alemania y vida diaria|آلمان و زندگی روزمره|Jerman dan keseharian|Allemagne et quotidien|Almanya û jiyana rojane|Germania și viața cotidiană|Германия и ежедневие|Germania e vita quotidiana|Njemačka i svakodnevica|Nemačka i svakodnevica|آلمان و زندگی روزمره|Njemačka i svakodnevica|Đức và đời sống|Németország és mindennapok|Γερμανία και καθημερινότητα',
};


function parseRows<T extends readonly string[]>(
  keys: T,
  rows: Record<string, string>,
): Record<string, Record<T[number], string>> {
  return Object.fromEntries(
    Object.entries(rows).map(([language, raw]) => {
      const values = raw.split('|');

      if (values.length !== keys.length) {
        throw new Error(`Invalid learning dictionary: ${language}`);
      }

      return [
        language,
        Object.fromEntries(
          keys.map((key, index) => [key, values[index]])
        ),
      ];
    })
  ) as Record<string, Record<T[number], string>>;
}


const COPY = parseRows(COPY_KEYS, COPY_RAW);


const TERMS = Object.fromEntries(
  Object.entries(TERM_RAW).map(([term, raw]) => {
    const values = raw.split('|');

    if (values.length !== LANGUAGES.length) {
      throw new Error(`Invalid learning term: ${term}`);
    }

    return [
      term,
      Object.fromEntries(
        LANGUAGES.map((language, index) =>
          [language, values[index]]
        )
      ),
    ];
  })
) as Record<string, Record<string, string>>;


type CueId =
  | 'which'
  | 'who'
  | 'what'
  | 'where'
  | 'when'
  | 'how'
  | 'why'
  | 'permission'
  | 'obligation'
  | 'ability'
  | 'negative'
  | 'only'
  | 'except'
  | 'because'
  | 'purpose'
  | 'condition'
  | 'contrast'
  | 'sequence';


const QUESTION_RULES: Array<[CueId, RegExp]> = [
  ['which', /\bwelch(?:e|er|es|en|em)?\b/i],
  ['who', /\bwer\b/i],
  ['what', /\bwas\b/i],
  ['where', /\bwo(?:hin|her)?\b/i],
  ['when', /\bwann\b/i],
  ['how', /\bwie\b/i],
  ['why', /\bwarum\b/i],
  ['because', /\b(?:weil|denn)\b/i],
  ['purpose', /\bdamit\b/i],
  ['condition', /\b(?:wenn|falls)\b/i],
  ['contrast', /\b(?:obwohl|aber)\b/i],
  ['sequence', /\b(?:zuerst|danach)\b/i],
];


const ATTENTION_RULES: Array<[CueId, RegExp]> = [
  ['negative', /\b(?:nicht|kein(?:e|er|es|en|em)?)\b/i],
  ['only', /\bnur\b/i],
  ['except', /\baußer\b/i],
  ['obligation', /\b(?:muss|müssen|musst|soll|sollen)\b/i],
  ['permission', /\b(?:darf|dürfen|darfst)\b/i],
  ['ability', /\b(?:kann|können|kannst)\b/i],
];


const THEME_RULES: Array<[
  Exclude<LearningVisualKind, 'task' | 'constraint' | 'answer'>,
  keyof typeof TERM_RAW,
  RegExp,
]> = [
  [
    'rights',
    'rights',
    /\b(?:Grundgesetz|Grundrecht|Recht|Freiheit|Wahl|wählen|Gleichberechtigung|Menschenrecht)\w*\b/i,
  ],
  [
    'institution',
    'institutions',
    /\b(?:Bundestag|Bundesrat|Bundesregierung|Regierung|Bundespräsident|Parlament|Gericht|Polizei|Behörde|Amt)\w*\b/i,
  ],
  [
    'history',
    'history',
    /\b(?:DDR|Nationalsozialismus|Mauer|Wiedervereinigung|Geschichte|Jahr|Jahre|1949|1989|1990)\w*\b/i,
  ],
  [
    'society',
    'society',
    /\b(?:Religion|Familie|Schule|Arbeit|Integration|Verein|Gewerkschaft|Medien)\w*\b/i,
  ],
  [
    'state',
    'federalState',
    /\b(?:Bundesland|Landtag|Ministerpräsident|Gemeinde|Kommune|Stadt|Kreis)\w*\b/i,
  ],
  [
    'everyday',
    'everyday',
    /\b(?:Deutschland|Bürger|Bürgerin|Menschen|Person)\w*\b/i,
  ],
];


function term(
  id: string,
  language: string,
): string {
  return TERMS[id]?.[language]
    || TERMS[id]?.en
    || id;
}


function matchRule<T extends string>(
  value: string,
  rules: Array<[T, RegExp]>,
): { id: T; german: string } | null {
  for (const [id, expression] of rules) {
    const matched = value.match(expression);

    if (matched) {
      return { id, german: matched[0] };
    }
  }

  return null;
}


export function getLearningCopy(
  language: string,
): LearningCopy {
  return (COPY[language] || COPY.en) as unknown as LearningCopy;
}


export function buildLearningNodes(
  question: string,
  language: string,
): LearningNode[] {
  const copy = getLearningCopy(language);
  const nodes: LearningNode[] = [];
  const questionCue = matchRule(question, QUESTION_RULES);

  nodes.push({
    kind: 'task',
    label: copy.questionPattern,
    german: questionCue?.german
      || question.split(/[,:?]/)[0].split(/\s+/).slice(0, 4).join(' '),
    meaning: questionCue
      ? term(questionCue.id, language)
      : copy.followFlow,
  });

  const attentionCue = matchRule(question, ATTENTION_RULES);

  if (attentionCue) {
    nodes.push({
      kind: 'constraint',
      label: copy.attention,
      german: attentionCue.german,
      meaning: term(attentionCue.id, language),
    });
  }

  for (const [kind, termId, expression] of THEME_RULES) {
    const matched = question.match(expression);

    if (matched) {
      nodes.push({
        kind,
        label: copy.topic,
        german: matched[0],
        meaning: term(termId, language),
      });
      break;
    }
  }

  if (nodes.length === 1) {
    nodes.push({
      kind: 'everyday',
      label: copy.topic,
      german: question.match(/\b[A-ZÄÖÜ][a-zäöüß]{3,}\b/)?.[0]
        || question.split(/\s+/).slice(0, 2).join(' '),
      meaning: term('everyday', language),
    });
  }

  return nodes;
}
